import { User } from '@/store/auth/auth.interface';
import { Resource } from './library';
import { StatusVariant } from '@/components/ui/status';

export enum AgentStepEnum {
  UPLOAD_RESUME,
  INFORMATION,
  AGENT_KNOWLEDGE,
  WEBSITE_INTEGRATION,
  DATA_INTEGRATION,
  TEAM_MEMBER,
  SETTING,
  TRAINING,
  SYNTHESIZING_KNOWLEDGE,
}

export const AgentStep = {
  [AgentStepEnum.UPLOAD_RESUME]: 'Upload Resume',
  [AgentStepEnum.INFORMATION]: 'Information',
  [AgentStepEnum.AGENT_KNOWLEDGE]: 'Agent Knowledge',
  [AgentStepEnum.WEBSITE_INTEGRATION]: 'Website Integration',
  [AgentStepEnum.DATA_INTEGRATION]: 'Data Integration',
  [AgentStepEnum.TEAM_MEMBER]: 'Team Member',
  [AgentStepEnum.SETTING]: 'Setting',
  [AgentStepEnum.SYNTHESIZING_KNOWLEDGE]: 'Synthesizing Knowledge',
} as const;

export enum AgentStatusEnum {
  init = 'init',
  training = 'training',
  updating = 'updating',
  success = 'success',
  active = 'active',
  demo = 'demo',
  draft = 'draft',
  inactive = 'inactive',
  deleted = 'deleted',
  expired = 'expired',
}

export const AgentStatus: Record<AgentStatusEnum, string> = {
  init: 'agent.status.init',
  training: 'agent.status.training',
  updating: 'agent.status.updating',
  success: 'agent.status.active',
  active: 'agent.status.active',
  demo: 'agent.status.demo',
  draft: 'agent.status.draft',
  inactive: 'agent.status.inactive',
  deleted: 'agent.status.deleted',
  expired: 'agent.status.expired',
};

export const AgentTag: Record<AgentStatusEnum, StatusVariant> = {
  init: 'sky',
  training: 'warning',
  updating: 'warning',
  success: 'success',
  active: 'success',
  demo: 'default',
  draft: 'default',
  inactive: 'default',
  deleted: 'default',
  expired: 'default',
};

export type AgentType = 'general' | 'specialized' | 'dana' | 'tabular' | 'financial' | 'adaptive';

export enum AgentTypeEnum {
  GENERAL = 'general',
  TABULAR = 'tabular',
  SPECIALIZED = 'specialized',
  DANA = 'dana',
  FINANCIAL = 'financial',
  ADAPTIVE = 'adaptive',
}

export const AgentTypeMapping = {
  [AgentTypeEnum.GENERAL]: 'GMA',
  [AgentTypeEnum.TABULAR]: 'TABULAR',
  [AgentTypeEnum.SPECIALIZED]: 'SSA',
  [AgentTypeEnum.DANA]: 'DANA',
  [AgentTypeEnum.FINANCIAL]: 'FINANCIAL',
  [AgentTypeEnum.ADAPTIVE]: 'ADAPTIVE',
} as const;

// Website integration types
export type Article = {
  article_id: string;
  name: string;
  description: string;
  url: string;
  date: string;
};

export type Website = {
  website_id: string;
  title?: string;
  url: string;
  description?: string;
  articles: Article[];
};

// Data integration types
export type ProcessParameter = {
  parameter_id: string;
  name: string;
  value: string;
  sampling: string;
  isSelected?: boolean;
};

export type DataIntegration = {
  data_integration_id: string;
  name: string;
  method: string;
  connection_url: string;
  description: string;
  status: string;
};

// Agent configuration types
export type GeneralAgentConfig = {
  level: string;
  sub_agents?: any;
  is_default: boolean;
  data_integrations: DataIntegration[];
  visible_to_users?: boolean;
  prosea_type?: string;
  top_k?: number;
  filter?: boolean;
  trace_ground_truth?: boolean;
  code_interpreter?: boolean;
  use_synthesys?: boolean;
  use_pk_only?: boolean;
  dana_code?: string;
};

export type AgentResource = {
  id: number;
  agent_id: number;
  agent?: Agent;
  resource_id: number;
  resource: Resource;
  data: Record<string, any>;
  is_deleted: boolean;
};

// Agent improvement and evaluation types
export type AgentImprovement = {
  id: number;
  agent_id: number;
  created_at: string;
  updated_at: string;
  user_id: number;
  type: string;
  status: string;
  data: Record<string, string>;
  is_deleted: boolean;
  orginalMessage: any;
  original_message_id: number;
};

export type EvaluationResult = {
  id: number;
  title?: string;
  agent: Agent;
  agent_id: number;
  judgement_type: 'numerical' | 'binary';
  user?: User;
  evaluation_result: {
    selectedAnalysis: Record<string, boolean>;
    satisfactory_level: number;
  };
  created_at: string;
  updated_at: string;
  status: string;
};

// Base interface for common agent properties
export interface BaseAgent {
  id: number | null;
  name?: string;
  description?: string;
  agent_id?: string;
  agent_type?: AgentType | null;
  general_agent_config?: GeneralAgentConfig;
  status?: string;
  is_official?: boolean;
  made_official_by?: any;
  made_official_by_id?: number;
  is_financial_agent?: boolean;
}

// Main Agent interface extends base with full properties
export interface Agent extends BaseAgent {
  is_published?: boolean;
  user_id?: number;
  user?: User;

  // Configuration
  embedding_config?: Record<string, any>;
  base_model?: string;
  model_size?: string;
  learning_rate?: number;
  compression?: string;
  indexing_method?: Record<string, any>;

  // Resources
  avatar?: string;
  documents?: string;
  document?: {
    name: string;
  };
  files?: {
    name: string;
    size: number;
  }[];
  agent_resources?: AgentResource[];

  // Knowledge
  heuristics?: { condition: string; action: string }[];
  general_heuristics?: Record<string, any>;
  suggested_questions?: string[] | any[];
  capabilities?: Record<string, any>;
  knowledge?: {
    prior_knowledges?: {
      question: string;
      answer: string;
    }[];
  };

  // Evaluation and improvements
  evaluation_results?: EvaluationResult[];
  improvements?: AgentImprovement[];
  metric?: Record<string, string | number>;

  // Other
  temporary_changes?: Record<string, any>;
  sub_agents?: Agent[];
  created_at?: string;
  updated_at?: string;
}

// Form interface extends base with form-specific fields
export interface AgentForm extends BaseAgent {
  step: AgentStepEnum;
  previousStep: AgentStepEnum | null;
  resources: Resource[];
  agent_resources?: Resource[];
  websites: Website[];
  currentWebsite: Website | null;
  synthesysKnowledgeData: any;
  cv_file_s3_url?: string;
  isCreateAgent?: boolean;
  general_agent_config: GeneralAgentConfig;
  avatar?: string;
  supporting_documents?: string[];
  isOpenComplementaryFiles?: boolean;
  base_model?: string;
  model_size?: string;
}

export interface ReportedIssue {
  id: number;
  issue: string;
  reportedBy: string;
  reportedTime: string;
  activities: string;
  status: string;
  solvedBy: string;
  parent_message: {
    message: string;
  };
  created_at: string;
  user: User;
  mark_done_user: User;
  original_message: {
    message: string;
    messages_feedbacks: {
      feedback: string;
      type: string;
      id: number;
      reasons?: string;
    }[];
    meta_data: {
      is_dana: boolean;
      message_type: string;
      result: {
        content: string;
      };
      executed_plan: {
        content: string;
      };
      solution: string;
    };
  };
  type: string;
  data: {
    answer: string;
    resources: Resource[];
    reasons?: string;
  };
  agent: Agent;
  agent_id: number;
  updated_at: string;
}
